/**
 * Ask the browser's password manager to save the sign-in (Chrome/Edge: the
 * "Save password?" prompt) and to offer a saved one on the login page. The app
 * signs in without a page load, so the browser's own detection can miss it;
 * the Credential Management API makes it explicit. Safari/Firefox lack
 * PasswordCredential and simply keep their usual autofill.
 */
interface PasswordCredentialLike extends Credential {
  password?: string;
}
type PasswordCredentialCtor = new (data: { id: string; password: string; name?: string }) => Credential;

function ctor(): PasswordCredentialCtor | null {
  const w = window as unknown as { PasswordCredential?: PasswordCredentialCtor };
  return typeof w.PasswordCredential === "function" && navigator.credentials ? w.PasswordCredential : null;
}

export async function saveSignIn(email: string, password: string): Promise<void> {
  const C = ctor();
  if (!C) return;
  try {
    await navigator.credentials.store(new C({ id: email, password, name: email }));
  } catch {
    // the user declined or the browser refused: nothing to do
  }
}

/** A saved email/password the user picks (or the browser fills silently), else null. */
export async function savedSignIn(): Promise<{ email: string; password: string } | null> {
  if (!ctor()) return null;
  try {
    const c = (await navigator.credentials.get({
      password: true,
      mediation: "optional",
    } as CredentialRequestOptions)) as PasswordCredentialLike | null;
    return c && c.id && c.password ? { email: c.id, password: c.password } : null;
  } catch {
    return null;
  }
}
